/**
 * L.15.6 — NIPE tool: describeSpecializationControls
 *
 * Read-only. Returns the canonical "what does every Tinker knob do"
 * primer text, optionally focused on a single control and tuned for
 * audience (attorney | operator).
 *
 * Source of truth: `src/topics/tinker-api.md`. The body here mirrors
 * the markdown's structure so NIPE chat output stays consistent with
 * the topic tile and the (?) popovers in the Specialization Lab.
 *
 * When the Specialization Lab is open in the SPA, this tool also
 * fetches the user's current settings via the read-only
 * `window.__repancySpecializationLab` bridge so NIPE can answer
 * "what's MY current learning rate?" — without ever being able to
 * change it.
 *
 * Stays a leaf module per the NIPE tool contract.
 */

import type { NipeTool } from './types';
import tinkerApiTopicRaw from '../../../topics/tinker-api.md?raw';
import type { ControlDescription } from '../../../components/specialization-lab/types';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface DescribeSpecializationControlsContext {}

type ControlKey =
  | 'baseModel'
  | 'loraRank'
  | 'epochs'
  | 'learningRate'
  | 'costCapUsd'
  | 'datasetScope';

const VALID_KEYS: ReadonlyArray<ControlKey> = [
  'baseModel',
  'loraRank',
  'epochs',
  'learningRate',
  'costCapUsd',
  'datasetScope',
];

/**
 * Strip the YAML frontmatter from a topic file. Mirrors `splitFrontmatter`
 * in `lib/vault/parse.ts` but inlined to keep this leaf module
 * dependency-free (the chat runner shouldn't pull the vault parser).
 */
function stripFrontmatter(raw: string): string {
  if (!raw.startsWith('---')) return raw;
  const end = raw.indexOf('\n---', 3);
  if (end === -1) return raw;
  return raw.slice(end + 4).replace(/^\s*\n/, '');
}

const TOPIC_BODY = stripFrontmatter(tinkerApiTopicRaw);

/** Extract the `## <heading>` section corresponding to a control key. */
function extractControlSection(controlKey: ControlKey): string {
  const heading = headingFor(controlKey);
  // Match starting at the heading line through the next `## ` (exclusive)
  // or end of file. Don't use the `m` flag: with multiline `$` matches
  // end-of-LINE, which terminates the lazy quantifier immediately after
  // the heading and returns an empty section. We anchor with `\n## ` at
  // the front of TOPIC_BODY instead (the file always starts with at
  // least one newline post-frontmatter-strip, and every section heading
  // is preceded by a blank line in tinker-api.md).
  const escaped = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`\\n## ${escaped}\\n([\\s\\S]*?)(?=\\n## |$)`);
  const match = TOPIC_BODY.match(re);
  if (!match || !match[1]) return TOPIC_BODY;
  return `## ${heading}\n${match[1].trim()}`;
}

function headingFor(controlKey: ControlKey): string {
  switch (controlKey) {
    case 'baseModel':
      return 'Base model';
    case 'loraRank':
      return 'LoRA rank';
    case 'epochs':
      return 'Epochs';
    case 'learningRate':
      return 'Learning rate';
    case 'costCapUsd':
      return 'Cost cap (USD)';
    case 'datasetScope':
      return 'Dataset scope';
  }
}

interface LabBridgeShape {
  currentSettings: () => unknown;
  describeControls: () => ControlDescription[];
  isOpen: () => boolean;
}

function readLabBridge(): LabBridgeShape | null {
  if (typeof window === 'undefined') return null;
  const bridge = (window as unknown as { __repancySpecializationLab?: LabBridgeShape })
    .__repancySpecializationLab;
  return bridge ?? null;
}

export const describeSpecializationControlsTool: NipeTool<DescribeSpecializationControlsContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'describeSpecializationControls',
      description:
        "Return the canonical explanation of the Specialization Lab's training controls (base model, LoRA rank, epochs, learning rate, cost cap, dataset scope) and the underlying Tinker SDK call surface. Use when the user asks 'what does this knob do?' or 'how should I set this?'. If the Lab is open the response also reports the user's current values so you can ground the answer. NEVER mutates settings — the user owns the sliders.",
      parameters: {
        type: 'object',
        properties: {
          controlKey: {
            type: 'string',
            description:
              "Optional. One of 'baseModel', 'loraRank', 'epochs', 'learningRate', 'costCapUsd', 'datasetScope'. When set, the response focuses on that one knob; otherwise it returns the full primer.",
          },
          audience: {
            type: 'string',
            description:
              "Optional. 'attorney' (default) for plain-language framing; 'operator' for the technical Tinker SDK surface.",
          },
        },
        required: [],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, _ctx) => {
    const audience =
      typeof args.audience === 'string' && args.audience.toLowerCase() === 'operator'
        ? 'operator'
        : 'attorney';

    const requestedKey =
      typeof args.controlKey === 'string' &&
      VALID_KEYS.includes(args.controlKey as ControlKey)
        ? (args.controlKey as ControlKey)
        : null;

    const attorneyBody = (
      TOPIC_BODY.split(
        '## The full Tinker SDK call surface (operator reference)',
      )[0] ?? TOPIC_BODY
    ).trim();
    const body = requestedKey
      ? extractControlSection(requestedKey)
      : audience === 'operator'
        ? TOPIC_BODY
        : attorneyBody;

    const bridge = readLabBridge();
    const labOpen = bridge?.isOpen() ?? false;
    const currentSettings = bridge ? bridge.currentSettings() : null;
    const currentControlDescriptions = bridge ? bridge.describeControls() : null;
    const focusedCurrent =
      requestedKey && currentControlDescriptions
        ? currentControlDescriptions.find((c) => c.key === requestedKey) ?? null
        : null;

    return {
      audience,
      controlKey: requestedKey,
      body,
      labOpen,
      currentSettings,
      currentControl: focusedCurrent,
    };
  },
};
